/**
 * Expense Category Editor
 * =======================
 * 
 * Allows users to view and correct AI-categorized expense line items.
 * Part of P2: P&L Edit UI feature.
 * 
 * Features:
 * - View parsed expenses grouped by category
 * - Change category assignments
 * - Track confidence scores
 * - Save corrections to case
 * - Trigger re-calculation with corrected data
 */

import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

// Expense category definitions matching backend ExpenseCategory enum
const EXPENSE_CATEGORIES = [
  { value: 'officer_compensation', label: 'Officer Compensation', description: 'Owner/officer salary - often reduced during closure', color: 'bg-blue-100' },
  { value: 'salaried_wages', label: 'Salaried Wages', description: 'Management salaries - may be retained or laid off', color: 'bg-blue-50' },
  { value: 'hourly_wages', label: 'Hourly Wages', description: 'Line staff - usually 0% during extended closures', color: 'bg-cyan-50' },
  { value: 'rent', label: 'Rent', description: 'Lease obligations - typically 100% continuation', color: 'bg-green-100' },
  { value: 'utilities', label: 'Utilities', description: 'Minimal for preservation - 10-40%', color: 'bg-green-50' },
  { value: 'insurance', label: 'Insurance', description: 'Reduced but needed - 60-100%', color: 'bg-yellow-100' },
  { value: 'professional_services', label: 'Professional Services', description: 'Legal/accounting - INCREASES during claims', color: 'bg-yellow-50' },
  { value: 'depreciation', label: 'Depreciation', description: 'Non-cash expense - accounting entry only', color: 'bg-gray-100' },
  { value: 'debt_service', label: 'Debt Service', description: 'Loan payments - obligated, 100%', color: 'bg-red-100' },
  { value: 'taxes_licenses', label: 'Taxes & Licenses', description: 'Property tax, licenses - obligated, 100%', color: 'bg-red-50' },
  { value: 'other', label: 'Other', description: 'Discretionary, mostly eliminated (0-50%)', color: 'bg-gray-50' },
];

interface ExpenseItem {
  id: string;
  original_label: string;
  amount: number;
  category: string;
  expense_category: string | null;
  confidence: number;
  period_label: string;
  period_start: string;
}

interface CategorySummary {
  category: string;
  total: number;
  item_count: number;
  avg_confidence: number;
}

interface ExpenseCategoryEditorProps {
  caseId: string;
  onSave?: (corrections: ExpenseCorrection[]) => void;
  onRecalculate?: () => void;
  readOnly?: boolean;
}

interface ExpenseCorrection {
  item_id: string;
  original_category: string;
  new_category: string;
  reason?: string;
}

export default function ExpenseCategoryEditor({
  caseId,
  onSave,
  onRecalculate,
  readOnly = false
}: ExpenseCategoryEditorProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expenses, setExpenses] = useState<ExpenseItem[]>([]);
  const [corrections, setCorrections] = useState<Map<string, string>>(new Map());
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'amount' | 'confidence' | 'category'>('confidence');
  const [showLowConfidenceOnly, setShowLowConfidenceOnly] = useState(false);

  // Load expense data
  useEffect(() => {
    console.log('ExpenseCategoryEditor: Loading expenses for case:', caseId);
    loadExpenses();
  }, [caseId]);

  const loadExpenses = async () => {
    console.log('ExpenseCategoryEditor: loadExpenses started');
    setLoading(true);
    setError(null);
    try {
      console.log('ExpenseCategoryEditor: Calling API...');
      const response = await api.get<{success: boolean; expenses: ExpenseItem[]; total_count?: number; low_confidence_count?: number; message?: string}>(`/api/v1/document-pipeline/pl-expenses/${caseId}`);
      console.log('ExpenseCategoryEditor: API response:', response);
      if (response && response.success) {
        console.log('ExpenseCategoryEditor: Setting expenses:', response.expenses?.length);
        setExpenses(response.expenses || []);
      } else {
        console.log('ExpenseCategoryEditor: Response not successful:', response);
        setError(response?.message || 'Failed to load expenses');
      }
    } catch (err: any) {
      console.error('ExpenseCategoryEditor: Error loading expenses:', err);
      setError(err.message || 'Failed to load expense data');
    } finally {
      console.log('ExpenseCategoryEditor: Setting loading to false');
      setLoading(false);
    }
  };

  // Calculate category summaries
  const categorySummaries: CategorySummary[] = EXPENSE_CATEGORIES.map(cat => {
    const items = expenses.filter(e => 
      (corrections.get(e.id) || e.expense_category || e.category) === cat.value
    );
    return {
      category: cat.value,
      total: items.reduce((sum, e) => sum + e.amount, 0),
      item_count: items.length,
      avg_confidence: items.length > 0 
        ? items.reduce((sum, e) => sum + e.confidence, 0) / items.length 
        : 0
    };
  });

  // Filter and sort expenses
  const filteredExpenses = expenses
    .filter(e => {
      if (filter !== 'all' && (corrections.get(e.id) || e.expense_category || e.category) !== filter) {
        return false;
      }
      if (showLowConfidenceOnly && e.confidence >= 0.7) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'amount':
          return b.amount - a.amount;
        case 'confidence':
          return a.confidence - b.confidence; // Low confidence first
        case 'category':
          return (a.expense_category || '').localeCompare(b.expense_category || '');
        default:
          return 0;
      }
    });

  const handleCategoryChange = (itemId: string, newCategory: string) => {
    const newCorrections = new Map(corrections);
    const item = expenses.find(e => e.id === itemId);
    if (item && newCategory !== (item.expense_category || item.category)) {
      newCorrections.set(itemId, newCategory);
    } else {
      newCorrections.delete(itemId);
    }
    setCorrections(newCorrections);
  };

  const handleSave = async () => {
    if (corrections.size === 0) return;
    
    setSaving(true);
    try {
      const correctionsList: ExpenseCorrection[] = [];
      corrections.forEach((newCategory, itemId) => {
        const item = expenses.find(e => e.id === itemId);
        if (item) {
          correctionsList.push({
            item_id: itemId,
            original_category: item.expense_category || item.category,
            new_category: newCategory,
            reason: 'User correction'
          });
        }
      });

      const response = await api.post<{success: boolean; corrections_applied?: number; message?: string}>(`/api/v1/document-pipeline/pl-expenses/${caseId}/corrections`, {
        corrections: correctionsList
      });

      if (response.success) {
        setCorrections(new Map());
        loadExpenses(); // Reload with corrections applied
        onSave?.(correctionsList);
      } else {
        setError(response.message || 'Failed to save corrections');
      }
    } catch (err: any) {
      console.error('Error saving corrections:', err);
      setError(err.message || 'Failed to save corrections');
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async () => {
    try {
      await api.post(`/v1/future-closure/recalculate/${caseId}`);
      onRecalculate?.();
    } catch (err: any) {
      console.error('Error triggering recalculation:', err);
      setError('Failed to trigger recalculation');
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-600 bg-green-50';
    if (confidence >= 0.5) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getCategoryInfo = (value: string) => {
    return EXPENSE_CATEGORIES.find(c => c.value === value) || EXPENSE_CATEGORIES[EXPENSE_CATEGORIES.length - 1];
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow">
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading expense data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-white rounded-lg shadow">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-red-700">{error}</span>
          </div>
          <button
            onClick={loadExpenses}
            className="mt-3 px-4 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (expenses.length === 0) {
    return (
      <div className="p-6 bg-white rounded-lg shadow">
        <div className="text-center py-10">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No P&L Data</h3>
          <p className="mt-1 text-sm text-gray-500">
            Upload a P&L statement to view and edit expense categories.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Expense Category Editor</h3>
            <p className="mt-1 text-sm text-gray-500">
              Review and correct AI-categorized expenses for accurate Future Closure calculations
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {corrections.size > 0 && (
              <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                {corrections.size} pending changes
              </span>
            )}
            {!readOnly && corrections.size > 0 && (
              <>
                <button
                  onClick={() => setCorrections(new Map())}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  Discard
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Corrections'}
                </button>
              </>
            )}
            {!readOnly && (
              <button
                onClick={handleRecalculate}
                className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50"
              >
                Recalculate
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Category Summary Cards */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Category Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {categorySummaries.filter(s => s.item_count > 0).map(summary => {
            const catInfo = getCategoryInfo(summary.category);
            return (
              <button
                key={summary.category}
                onClick={() => setFilter(filter === summary.category ? 'all' : summary.category)}
                className={`p-3 rounded-lg text-left transition-all ${
                  filter === summary.category 
                    ? 'ring-2 ring-blue-500 bg-white shadow' 
                    : `${catInfo.color} hover:shadow`
                }`}
              >
                <div className="text-xs font-medium text-gray-500 truncate">{catInfo.label}</div>
                <div className="text-lg font-semibold text-gray-900">{formatCurrency(summary.total)}</div>
                <div className="text-xs text-gray-500">{summary.item_count} items</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={showLowConfidenceOnly}
              onChange={(e) => setShowLowConfidenceOnly(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-gray-600">Show low confidence only</span>
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="confidence">Sort by Confidence</option>
            <option value="amount">Sort by Amount</option>
            <option value="category">Sort by Category</option>
          </select>
        </div>
        <div className="text-sm text-gray-500">
          Showing {filteredExpenses.length} of {expenses.length} items
        </div>
      </div>

      {/* Expense Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Period
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Confidence
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredExpenses.map((item) => {
              const currentCategory = corrections.get(item.id) || item.expense_category || item.category;
              const isModified = corrections.has(item.id);
              const catInfo = getCategoryInfo(currentCategory);
              
              return (
                <tr key={item.id} className={isModified ? 'bg-blue-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={item.original_label}>
                      {item.original_label}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {formatCurrency(item.amount)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {item.period_label || item.period_start}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceColor(item.confidence)}`}>
                      {Math.round(item.confidence * 100)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {readOnly ? (
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${catInfo.color}`}>
                        {catInfo.label}
                      </span>
                    ) : (
                      <select
                        value={currentCategory}
                        onChange={(e) => handleCategoryChange(item.id, e.target.value)}
                        className={`text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                          isModified ? 'ring-2 ring-blue-500' : ''
                        }`}
                      >
                        {EXPENSE_CATEGORIES.map(cat => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer with Legend */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Category Legend</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
          {EXPENSE_CATEGORIES.slice(0, 8).map(cat => (
            <div key={cat.value} className="flex items-start space-x-2">
              <div className={`w-3 h-3 rounded ${cat.color} mt-0.5`}></div>
              <div>
                <span className="font-medium text-gray-700">{cat.label}</span>
                <span className="text-gray-500 ml-1">- {cat.description}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
